import React, { useState } from 'react';
import PropTypes from 'prop-types';
import fileSaver from 'file-saver';
import { convertBase64ToByteArray } from 'utils/functions';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { GET_FILE_FOR_PREVIEW_QUERY } from '../queries';
import { CloudDownloadOutlined, RotateLeft, RotateRight } from '@mui/icons-material';
import PDFPreviewComponent from './PDFPreviewComponent';
import { buildDMSDownloadUrl } from '../actionCreators';
import { FakeText, Dialog, IconButton, Typography } from '@totalsoft/rocket-ui';
import { BackdropCustom, DialogButton, DialogTitle, ImageCanvasStyled } from './DocumentsStyles';
import PreviewZoomComponent from './PreviewZoomComponent';

const DocumentUploadPreviewModal = ({ open, fileId, onCloseModal, openFromRoute }) => {
  const { t } = useTranslation();
  const [rotationAngle, setRotationAngle] = useState(0);
  const [scale, setScale] = useState(1.0);
  const zoomableFileTypes = ['pdf', 'jpg', 'jpeg', 'png'];

  const { data, loading } = useQueryWithErrorHandling(GET_FILE_FOR_PREVIEW_QUERY, {
    variables: { fileId },
    fetchPolicy: 'network-only',
    skip: !open
  });

  const type = data?.getFileForPreview?.contentType;

  const handleDownloadFile = () => {
    if (!data) return;

    const file = new Blob([convertBase64ToByteArray(data.getFileForPreview.content)], {
      type: 'application/octet-stream; base64'
    });

    fileSaver.saveAs(URL.createObjectURL(file), data?.getFileForPreview?.name);
  };

  const rotateRight = () => {
    setRotationAngle(prevAngle => prevAngle + 90);
  };

  const rotateLeft = () => {
    setRotationAngle(prevAngle => prevAngle - 90);
  };

  const previewComponent = type => {
    switch (type) {
      case 'pdf':
        return <PDFPreviewComponent rotationAngle={rotationAngle} viewportScale={scale} url={buildDMSDownloadUrl(fileId)()} />;
      case 'txt':
        return atob(data?.getFileForPreview?.content);
      case 'jpg':
      case 'png':
      case 'jpeg':
        return (
          <ImageCanvasStyled rotationAngle={rotationAngle} scale={scale}>
            <img src={`data:image/${type};base64,` + data?.getFileForPreview?.content} alt={data?.getFileForPreview?.name} width='100%' />
          </ImageCanvasStyled>
        );
      default:
        return (
          <Typography>
            {type
              ? t('OfferEdit.Documents.Preview.FileNotSupported', { docType: data?.getFileForPreview?.contentType })
              : t('OfferEdit.Documents.Preview.FileDownloadError')}
          </Typography>
        );
    }
  };

  return (
    <Dialog
      id='document-preview-dialog'
      open={open}
      onClose={onCloseModal}
      fullWidth
      maxWidth={'lg'}
      BackdropComponent={BackdropCustom}
      transitionDuration={0}
      showX={false}
      disableBackdropClick
      disableEscapeKeyDown
      actionsProps={{ sx: theme => ({ px: theme.spacing(3), pb: theme.spacing(2) }) }}
      title={
        <Grid container justifyContent={'space-between'}>
          <Grid item xs='auto'>
            <DialogTitle>{data?.getFileForPreview?.name}</DialogTitle>
          </Grid>
          <Grid item xs='auto'>
            {zoomableFileTypes.includes(type) && <PreviewZoomComponent scale={scale} setScale={setScale} />}
            <IconButton variant='text' color='default' onClick={rotateLeft} size='small'>
              <RotateLeft />
            </IconButton>
            <IconButton variant='text' color='default' onClick={rotateRight} size='small'>
              <RotateRight />
            </IconButton>
            <IconButton
              variant='text'
              color='default'
              onClick={handleDownloadFile}
              disabled={!data?.getFileForPreview}
              size='small'
              tooltip={t('OfferEdit.Documents.Preview.Download')}
            >
              <CloudDownloadOutlined />
            </IconButton>
          </Grid>
        </Grid>
      }
      content={loading ? <FakeText lines={5} /> : previewComponent(type)}
      actions={
        !openFromRoute && (
          <DialogButton color='primary' variant='contained' onClick={onCloseModal}>
            {t('OfferEdit.Documents.Preview.Close')}
          </DialogButton>
        )
      }
    />
  );
};

DocumentUploadPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  openFromRoute: PropTypes.bool,
  fileId: PropTypes.string,
  onCloseModal: PropTypes.func
};

export default DocumentUploadPreviewModal;
